import { As, Box, chakra, Flex, Icon, IconProps, Text } from "@chakra-ui/react";
import React from "react";
import { ManagerVerificationStatusEnumType } from "../../../../api/enums";
import { UnverifiedBadgeIcon } from "../../../../icons";
import { termsOfServiceUrl } from "../../../../links/urls";
import { VeriTooltip } from "../../../popover";
import BadgeIcon from "../../badge-icons/BadgeIcon";
import { VeriHeading } from "../../VeriHeading";
import { ManagerStatusWithTooltipProps } from "./ManagerStatusWithTooltip.types";

export const MANAGER_STATUS_DATA: Record<
  ManagerVerificationStatusEnumType,
  {
    message: string;
    header: string;
    getIcon: (props: IconProps) => React.ReactNode;
    isVerified: boolean;
    textColor: string;
    bgColor: string;
  }
> = {
  Unverified: {
    message:
      "Verivest has not performed any due diligence on this investment manager.",
    header: "Unverified Investment Manager",
    getIcon: (props: IconProps) =>
      getStatusIcon(UnverifiedBadgeIcon, { color: "icons.grey", ...props }),
    isVerified: false,
    textColor: "text.black",
    bgColor: "bg.greyLighter",
  },
  Verified: {
    message:
      "This investment manager’s principals have passed background checks and signed our Code of Conduct.",
    header: "Verivest Verified",
    getIcon: (props: IconProps) =>
      getStatusIcon(BadgeIcon, { color: "brand.main", ...props }),
    isVerified: true,
    textColor: "brand.main",
    bgColor: "bg.lightPurple2",
  },
};

const getStatusIcon = (icon: As, props: IconProps) => {
  return <Icon cursor="pointer" boxSize="20px" as={icon} {...props} />;
};

const ManagerStatusIconWithTooltip: React.FC<ManagerStatusWithTooltipProps> = ({
  status = ManagerVerificationStatusEnumType.Unverified,
  textOnly = true,
  onTooltipClick,
  iconProps,
  children,
  childrenOnly = true,
  tooltipPlacement = "top",
  tooltipIsOpenByDefault = false,

  // handy to keep this property for markup fixes
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  alwaysOpen: _alwaysOpen = false,
}) => {
  const [triggerRef, setTriggerRef] = React.useState<HTMLDivElement | null>(
    null,
  );
  const statusData = MANAGER_STATUS_DATA[status];
  const handleLearnMoreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };

  if (status !== ManagerVerificationStatusEnumType.Verified) {
    return <></>;
  }

  return (
    <Box ref={setTriggerRef}>
      <Flex alignItems={"center"}>
        {!childrenOnly || !children
          ? statusData.getIcon({
              boxSize: "20px",
              ...iconProps,
            })
          : ""}
        {children}
      </Flex>
      <VeriTooltip
        trigger={triggerRef || undefined}
        placement={tooltipPlacement}
        openByDefault={tooltipIsOpenByDefault}
      >
        <Box
          w={"100%"}
          maxW={"317px"}
          py={"8px"}
          px={"4px"}
          cursor={onTooltipClick ? "pointer" : "default"}
          display={"block"}
          _hover={{ textDecoration: "none" }}
          // WHy click? There is no pointerEvents click
          //pointerEvents={"click"}
          pointerEvents={"auto"}
          onClick={onTooltipClick}
        >
          {!textOnly && (
            <VeriHeading textStyle={"h6"} color={"text.black"}>
              {statusData.header}
              {statusData.getIcon({ ms: "8px" })}
            </VeriHeading>
          )}

          <Text mt={1} color={"text.grey"}>
            {statusData.message}
            <chakra.a
              onClick={handleLearnMoreClick}
              href={termsOfServiceUrl}
              target={"_blank"}
              px={"0.3em"}
              color={"brand.main"}
            >
              Learn more
            </chakra.a>
          </Text>
        </Box>
      </VeriTooltip>
    </Box>
  );
};

export default ManagerStatusIconWithTooltip;
